import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { fetchNetworkChat, sendNetworkChatMessage } from "../../../api/trustbuds";
import LoadingState from "../../../components/common/LoadingState";
import { formatRelativeTime } from "../utils/resolveSprintStateCopy";

function SprintMessageBubble({ message, isViewer, fallbackUsername }) {
  return (
    <Box sx={{ display: "flex", justifyContent: isViewer ? "flex-end" : "flex-start" }}>
      <Stack
        direction={isViewer ? "row-reverse" : "row"}
        spacing={1}
        alignItems="flex-end"
        sx={{ maxWidth: "86%" }}
      >
        {!isViewer ? (
          <Avatar
            src={message?.sender?.profile_image || ""}
            sx={{ width: 30, height: 30, fontSize: "0.8rem" }}
          >
            {(message?.sender?.username || fallbackUsername || "T").slice(0, 1).toUpperCase()}
          </Avatar>
        ) : null}

        <Box
          sx={{
            px: 1.6,
            py: 1.25,
            borderRadius: "8px",
            bgcolor: isViewer ? "#243f70" : "#f7f9fc",
            color: isViewer ? "#f8fbff" : "#102033",
            border: isViewer ? "none" : "1px solid #dde6f0",
          }}
        >
          <Typography
            variant="body2"
            sx={{ lineHeight: 1.55, color: isViewer ? "#f8fbff" : "#102033", fontWeight: 500 }}
          >
            {message?.body || ""}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.6,
              color: isViewer ? "rgba(248,251,255,0.72)" : "#70839a",
              fontWeight: 600,
            }}
          >
            {isViewer ? "You" : message?.sender?.username || fallbackUsername} ·{" "}
            {formatRelativeTime(message?.created_at)}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

export default function SprintChatPanel({
  sprintId,
  connectionId,
  partnerName,
  partnerUserId,
  viewerUserId,
  quickReplies = [],
  disabled = false,
  onActivity,
  composerFocusKey = 0,
}) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const visibleMessages = useMemo(() => messages.slice(-10), [messages]);

  const loadMessages = useCallback(async () => {
    if (!connectionId || disabled) {
      setMessages([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await fetchNetworkChat(connectionId, 10);
      setMessages(Array.isArray(data?.messages) ? data.messages : []);
    } catch (err) {
      setError(err?.message || "Unable to load chat.");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [connectionId, disabled]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (!composerFocusKey || disabled) return;
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }, 20);
    return () => clearTimeout(timer);
  }, [composerFocusKey, disabled]);

  async function handleSend(bodyOverride = "") {
    if (!connectionId || disabled) return;
    const nextBody = (bodyOverride || draft).trim();
    if (!nextBody) return;
    if (nextBody.length > 280) {
      setError("Message must be 280 characters or fewer.");
      return;
    }

    setSending(true);
    setError("");
    const tempId = `temp-${Date.now()}`;
    const tempMessage = {
      id: tempId,
      body: nextBody,
      created_at: new Date().toISOString(),
      sender: { id: viewerUserId || "viewer", username: "You" },
    };
    setMessages((prev) => [...prev, tempMessage].slice(-10));

    try {
      const response = await sendNetworkChatMessage(connectionId, nextBody, sprintId || null);
      const saved = response?.message || response;
      setMessages((prev) =>
        [...prev.filter((item) => String(item.id) !== tempId), saved].slice(-10)
      );
      setDraft("");
      onActivity?.();
    } catch (err) {
      setMessages((prev) => prev.filter((item) => String(item.id) !== tempId));
      setError(err?.message || "Unable to send message.");
    } finally {
      setSending(false);
    }
  }

  return (
    <Card
      variant="outlined"
      sx={{
        p: { xs: 2, md: 2.4 },
        borderRadius: "8px",
        borderColor: "#dce6f0",
        boxShadow: "0 20px 42px rgba(15, 23, 42, 0.08)",
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 850, letterSpacing: 0 }}>
              Respond to today
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.45, color: "#70839a" }}>
              Reply to today’s Daily Report.
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {quickReplies.map((reply) => (
            <Button
              key={reply}
              variant="outlined"
              color="inherit"
              onClick={() => handleSend(reply)}
              disabled={disabled}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                borderColor: "#dce6f0",
                color: "#4b5f79",
                bgcolor: "#f7f9fc",
                "&:hover": {
                  borderColor: "#cad8e7",
                  bgcolor: "#f3f7fb",
                },
              }}
            >
              {reply}
            </Button>
          ))}
        </Stack>

        {error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : null}

        <Box
          sx={{
            minHeight: 286,
            maxHeight: 430,
            overflowY: "auto",
            pr: 0.5,
          }}
        >
          <Stack spacing={1.15}>
            {disabled ? (
              <Typography variant="body2" color="text.secondary">
                Quick messages are unavailable right now.
              </Typography>
            ) : loading ? (
              <LoadingState
                title="Opening comments"
                body="Bringing the latest report responses into this Accountability."
                rows={2}
                compact
              />
            ) : visibleMessages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No responses yet. Use a quick reply when one of you submits.
              </Typography>
            ) : (
              visibleMessages.map((message) => {
                const isViewer = viewerUserId
                  ? String(message?.sender?.id || "") === String(viewerUserId)
                  : String(message?.sender?.id || "") !== String(partnerUserId || "");
                return (
                  <SprintMessageBubble
                    key={String(message.id)}
                    message={message}
                    isViewer={isViewer}
                    fallbackUsername={partnerName}
                  />
                );
              })
            )}
          </Stack>
        </Box>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} alignItems={{ md: "flex-end" }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            size="small"
            placeholder="Write a quick response..."
            value={draft}
            disabled={disabled}
            multiline
            maxRows={3}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                handleSend();
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                bgcolor: "#f7f9fc",
              },
            }}
          />
          <Button
            variant="contained"
            endIcon={<SendRoundedIcon />}
            onClick={() => handleSend()}
            disabled={disabled || sending}
            sx={{
              minWidth: 144,
              minHeight: 44,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 800,
            }}
          >
            {sending ? "Sending…" : "Send"}
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}
